/**
 * ⟦C48 · A-168 §3⟧ `EcranFenetre` : **la page-fenêtre** des écrans qui prennent tout le cadre
 * (crise, chute, fins, victoire, connexion). Avant ce chantier, chacun posait sa propre carte de
 * papier froissé sur l'aplat jaune du canevas (`.carte .carte-fin .froissee`) : la seule surface
 * du jeu, avec l'Atelier, qui ne ressemblait ni à la chambre ni aux fenêtres pixel. Matt,
 * 30/09/2026 : « Et il faut garder la bonne DA tout le long. »
 *
 * Le patron est celui de la chambre et de l'écran-titre, sans image nouvelle :
 * - le **fond** est celui du menu (`FondMenu`, la chambre `chambre-n1` du jeu, ⟦C55 · A-172⟧) sous le
 *   même voile sombre — l'écran de fin se pose SUR la chambre qu'on vient de quitter ;
 * - la carte est une `Fenetre` (`Fenetre.tsx`) : titre dans le bandeau violet, badge dans le
 *   bandeau (la petite étiquette qui était un post-it incliné), corps tramé, bordure en escalier ;
 * - le **pied** porte les boutons d'action : ils restent visibles sans défiler quelle que soit
 *   la longueur du texte (la victoire fait 1 300 px de haut sur 1280×800) — c'est le corps qui
 *   défile, jamais la page (même règle que `Panneau.tsx`, ⟦C44 · A-166⟧).
 *
 * Ce composant ne calcule rien et ne décide rien : il pose un cadre.
 */

import type { ReactNode } from "react";

import { FondMenu } from "../screens/Menu/FondMenu";
import { Fenetre } from "./Fenetre";

export interface EcranFenetreProps {
  /** Le titre, dans le bandeau (et titre de page : c'est un `<h1>`). */
  titre: string;
  /** Une icône avant le titre — un emoji, comme toutes les fenêtres du jeu. */
  icone?: ReactNode;
  /** La petite étiquette du bandeau (« tu n'es pas éliminé »), à droite du titre. */
  badge?: ReactNode;
  /** Les boutons d'action, dans le pied fixe de la fenêtre. */
  pied?: ReactNode;
  /** Classes de variante sur la `Fenetre` : `fenetre-crise`, `fenetre-victoire`, etc. */
  className?: string;
  /** Plus large (la victoire : la plaque partageable, le score décomposé). */
  large?: boolean;
  children: ReactNode;
}

export function EcranFenetre({ titre, icone, badge, pied, className, large, children }: EcranFenetreProps) {
  return (
    <main className="ecran-fenetre">
      <FondMenu />
      <div className="ecran-titre-voile" aria-hidden="true" />
      <div className="ecran-fenetre-scene">
        <Fenetre
          titre={titre}
          icone={icone}
          titreEnH1
          actions={badge ? <span className="fenetre-badge">{badge}</span> : undefined}
          pied={pied}
          className={`fenetre-ecran${large ? " fenetre-ecran-large" : ""}${className ? ` ${className}` : ""}`}
        >
          {children}
        </Fenetre>
      </div>
    </main>
  );
}
